import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Fragment,
  useEffect,
  useRef,
  useState,
  type DragEvent,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '@/app/auth/context';
import { Modal } from '@/components/admin/Modal';
import { Notice } from '@/components/admin/Notice';
import { ErrorState, SkeletonRows } from '@/components/admin/states';
import { ROLE_LABELS, type Role } from '@/features/auth/api';
import {
  deleteMessage,
  fetchThread,
  markThreadRead,
  postMessage,
  toggleMessageReaction,
  uploadMessageAttachment,
  REACTION_CHOICES,
  THREAD_KIND_DESCRIPTIONS,
  THREAD_KIND_LABELS,
  type MessageItem,
  type ThreadParticipant,
  type ThreadReadReceipt,
} from '@/features/messages/api';
import { PhotoPreview } from '@/features/members/MemberPhotoField';
import { ApiError } from '@/lib/api/http';
import { formatDateTime } from '@/lib/datetime';

function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)}KB`;
  return `${bytes}バイト`;
}

function isImageFileName(name: string | null | undefined): boolean {
  if (!name) return false;
  return /\.(jpe?g|png|gif|webp|bmp)$/i.test(name);
}

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** 参加者からプロフィール画面へ（D-235）。公開プロフィールが無いロールは null */
function participantProfileTo(p: ThreadParticipant, viewerRole: Role | null): string | null {
  if (p.member_id) return `/members/${p.member_id}`;
  if (viewerRole === 'office') {
    if (p.role === 'director') return `/directors/${p.user_id}/edit`;
    if (p.role === 'success') return `/success-staff/${p.user_id}/edit`;
  }
  return null;
}

/** 受講生なら会員詳細へのリンク付きで名前を出す（全画面共通の方針 / D-167） */
function NameLink({
  name,
  memberId,
  className,
}: {
  name: string;
  memberId: string | null;
  /** リンクのときだけ適用するクラス（hover:underline等）。非リンク時は付けない */
  className?: string;
}): ReactElement {
  if (memberId) {
    return (
      <Link to={`/members/${memberId}`} className={className}>
        {name}
      </Link>
    );
  }
  // 基調の文字色クラス（text-*）だけは非リンクにも残し、hover系だけ落とす
  const staticClass = (className ?? '')
    .split(' ')
    .filter((c) => !c.startsWith('hover:') && c !== 'text-wpaccent')
    .join(' ');
  return <span className={staticClass}>{name}</span>;
}

/** 同じ人の連投をひとまとめに見せる間隔（Slackと同じ5分） */
const GROUP_WINDOW_MS = 5 * 60 * 1000;

function dayKey(value: string): string {
  return new Date(value).toDateString();
}

/** 日付の区切り線に出すラベル。今日・昨日は相対表記にする（Slackと同じ） */
function formatDayLabel(value: string): string {
  const date = new Date(value);
  const today = new Date();
  if (date.toDateString() === today.toDateString()) return '今日';
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return '昨日';
  return date.toLocaleDateString('ja-JP', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    weekday: 'short',
  });
}

/** 時刻のみ。日付は区切り線が示すので、行には時刻だけ出す（Slackと同じ） */
function formatTimeOnly(value: string): string {
  return new Date(value).toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' });
}

/**
 * 本文中の `@参加者名` を強調表示する（グループ内の宛先指示 / D-166）。
 * 参加者名に一致するものだけを対象にし、それ以外の @〜 はそのまま出す。
 */
function renderBodyWithMentions(
  body: string,
  participantNames: string[],
  myName?: string | null,
): ReactNode {
  if (participantNames.length === 0) return body;
  // 「山田」と「山田太郎」のような部分一致の取り違えを避けるため、長い名前から先に当てる
  const sorted = [...participantNames].filter(Boolean).sort((a, b) => b.length - a.length);
  if (sorted.length === 0) return body;
  const pattern = new RegExp(`@(${sorted.map(escapeRegExp).join('|')})`, 'g');
  const parts = body.split(pattern);
  if (parts.length === 1) return body;
  return parts.map((part, index) => {
    if (index % 2 === 0) return part;
    // 自分が呼ばれたところだけ強く出す（Slackと同じ考え方 / D-318）
    const mine = Boolean(myName) && part === myName;
    return (
      <span
        key={`${index}-${part}`}
        className={
          mine
            ? 'rounded-sm bg-wpnotice-warning/30 px-0.5 font-semibold text-wptext-strong'
            : 'rounded-sm bg-wpaccent/15 px-0.5 font-semibold text-wpaccent'
        }
      >
        @{part}
      </span>
    );
  });
}

/**
 * 既読アイコンを「どのメッセージの下に出すか」を決める（FBメッセンジャーと同じ / D-145）。
 * その人が読んだ時刻以前の、最後のメッセージに割り当てる。
 */
function receiptsByMessageId(
  messages: MessageItem[],
  receipts: ThreadReadReceipt[],
): Map<string, ThreadReadReceipt[]> {
  const result = new Map<string, ThreadReadReceipt[]>();
  for (const receipt of receipts) {
    const readAt = new Date(receipt.read_at).getTime();
    let target: MessageItem | null = null;
    for (const message of messages) {
      if (new Date(message.created_at).getTime() <= readAt) target = message;
    }
    if (!target) continue;
    const list = result.get(target.id) ?? [];
    list.push(receipt);
    result.set(target.id, list);
  }
  return result;
}

/**
 * スレッドの会話表示 + 返信フォーム。
 * `ThreadDetailPage`（個人のメッセージ画面）と `InboxItemDetailPage`（共有受信箱）の
 * 両方から使い回す（internal_memo はこのコンポーネントには一切渡さない・含まれない）。
 */
export function ThreadConversation({
  threadId,
  currentUserId,
}: {
  threadId: string;
  currentUserId: string;
}): ReactElement {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['thread', threadId], queryFn: () => fetchThread(threadId) });
  const [draft, setDraft] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);
  // 絵文字を選ぶ小窓を出しているメッセージ（D-319）。ホバーでは見つけられないため
  // 入口の「＋」は常時出しておき、押したときだけ候補を開く
  const [pickerFor, setPickerFor] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // @メンションの入力補完（D-166）。null=補完を出していない / 文字列=@の後に打った検索語
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!file || !isImageFileName(file.name)) {
      setFilePreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setFilePreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  function updateMentionQuery(value: string, cursor: number): void {
    const before = value.slice(0, cursor);
    // カーソル直前が「@検索語」の途中なら候補を出す（@の前は行頭か空白）
    const match = /(?:^|[\s\n])@([^\s@]*)$/.exec(before);
    setMentionQuery(match ? (match[1] ?? '') : null);
  }

  function insertMention(name: string): void {
    const el = textareaRef.current;
    if (!el) return;
    const cursor = el.selectionStart;
    const before = draft.slice(0, cursor).replace(/@[^\s@]*$/, `@${name} `);
    const after = draft.slice(cursor);
    setDraft(before + after);
    setMentionQuery(null);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(before.length, before.length);
    });
  }

  useEffect(() => {
    if (query.data) void markThreadRead(threadId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, query.data !== undefined]);

  async function onSend(): Promise<void> {
    if (!draft.trim() && !file) return;
    setBusy(true);
    setError(null);
    try {
      // 先にファイルを預けてから、返ってきたキーを本文と一緒に投稿する
      const attachment = file ? await uploadMessageAttachment(threadId, file) : null;
      await postMessage(threadId, draft.trim(), attachment);
      setDraft('');
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await queryClient.invalidateQueries({ queryKey: ['thread', threadId] });
      await queryClient.invalidateQueries({ queryKey: ['threads'] });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'メッセージの送信に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  async function onToggleReaction(messageId: string, emoji: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await toggleMessageReaction(messageId, emoji);
      await queryClient.invalidateQueries({ queryKey: ['thread', threadId] });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'リアクションに失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  async function onDelete(message: MessageItem): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await deleteMessage(message.id);
      await queryClient.invalidateQueries({ queryKey: ['thread', threadId] });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '削除に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  function onDrop(e: DragEvent<HTMLDivElement>): void {
    e.preventDefault();
    setDragging(false);
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) setFile(dropped);
  }

  if (query.isPending) return <SkeletonRows rows={4} />;
  if (query.isError) {
    return (
      <ErrorState message="スレッドの取得に失敗しました。" onRetry={() => void query.refetch()} />
    );
  }

  const thread = query.data;
  const participants = thread.participants ?? [];
  const receipts = receiptsByMessageId(thread.messages, thread.read_receipts ?? []);
  const participantNames = participants
    .filter((p) => p.user_id !== currentUserId)
    .map((p) => p.name)
    .filter((n): n is string => Boolean(n));
  // 本文の強調で「自分が呼ばれたか」を見分けるために、自分の表示名も照合対象に入れる
  const myName = user?.display_name ?? null;
  const allNames = myName ? [...participantNames, myName] : participantNames;
  const mentionCandidates =
    mentionQuery === null
      ? []
      : participantNames.filter((name) => name.includes(mentionQuery)).slice(0, 8);

  function onDraftKeyDown(e: KeyboardEvent<HTMLTextAreaElement>): void {
    // @候補を出している間は Enter / Tab で先頭候補を確定、Escape で閉じる
    if (mentionCandidates.length > 0) {
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        const first = mentionCandidates[0];
        if (first) insertMention(first);
        return;
      }
      if (e.key === 'Escape') {
        setMentionQuery(null);
        return;
      }
    }
    // Enter で送信・Shift+Enter で改行（Slackと同じ）。
    // isComposing を見ないと、日本語の変換確定の Enter でそのまま送信されてしまう
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      if (!busy && (draft.trim() || file)) void onSend();
    }
  }

  return (
    <div
      className="flex flex-col gap-2"
      onDragOver={(e) => {
        if (!thread.can_post) return;
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
    >
      {/*
        会話のヘッダー。誰とのやり取りかと、そのスレッドの位置づけを1本のバーにまとめる
        （Slackのチャンネルヘッダーと同じ考え方。従来は2箱に分かれて縦を占めていた / D-056）
      */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-sm border border-wpborder bg-white px-3 py-2">
        {thread.kind === 'event_group' ? (
          <>
            <span className="font-semibold text-wptext-strong">参加 {participants.length}人</span>
            <button
              type="button"
              onClick={() => setMembersOpen(true)}
              className="text-wpaccent hover:underline"
            >
              参加者一覧
            </button>
          </>
        ) : (
          participants.map((p) => (
            <span key={p.user_id} className="flex items-center gap-1.5">
              <PhotoPreview photoUrl={p.photo_url} name={p.name ?? '（不明）'} size={24} />
              <NameLink
                name={p.name ?? '（不明）'}
                memberId={p.member_id}
                className="font-semibold text-wptext-strong hover:underline"
              />
              {p.role && <span className="text-xs text-wptext-muted">{ROLE_LABELS[p.role]}</span>}
            </span>
          ))
        )}
        <span className="ml-auto flex flex-wrap items-center gap-2 text-xs text-wptext-muted">
          <span className="rounded-sm border border-wpborder px-1.5 py-0.5">
            {THREAD_KIND_LABELS[thread.kind]}
          </span>
          {thread.event_title && <span>イベント: {thread.event_title}</span>}
          {thread.is_closed && (
            <span className="rounded-sm bg-wpborder/50 px-1.5 py-0.5 text-wptext-strong">
              クローズ済み
            </span>
          )}
          {thread.retention_until && (
            <span>保持期限: {new Date(thread.retention_until).toLocaleDateString('ja-JP')}</span>
          )}
        </span>
        <p className="w-full text-xs text-wptext-muted">{THREAD_KIND_DESCRIPTIONS[thread.kind]}</p>
      </div>

      <Modal open={membersOpen} title="参加者一覧" onClose={() => setMembersOpen(false)}>
        {participants.length === 0 ? (
          <p className="text-wptext-muted">参加者はいません。</p>
        ) : (
          <ul>
            {participants.map((p) => {
              const name = p.name ?? '（不明）';
              const to = participantProfileTo(p, user?.active_role ?? null);
              const body = (
                <span className="flex min-w-0 items-center gap-3">
                  <PhotoPreview photoUrl={p.photo_url} name={name} size={36} />
                  <span className="min-w-0">
                    <span className="font-semibold text-wptext-strong">{name}</span>
                    {p.user_id === currentUserId && (
                      <span className="ml-1 text-wptext-muted">（自分）</span>
                    )}
                    {p.role && (
                      <span className="ml-1 text-wptext-muted">（{ROLE_LABELS[p.role]}）</span>
                    )}
                  </span>
                </span>
              );
              return (
                <li key={p.user_id} className="border-b border-wpborder last:border-b-0">
                  {to ? (
                    <Link
                      to={to}
                      className="flex items-center py-2 hover:bg-wpcanvas"
                      onClick={() => setMembersOpen(false)}
                    >
                      {body}
                    </Link>
                  ) : (
                    <div className="flex items-center py-2">{body}</div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Modal>

      {error && (
        <Notice kind="error" dismissible={false}>
          {error}
        </Notice>
      )}

      {/*
        会話本体。吹き出しの左右振り分けはやめ、Slackと同じ「全員が同じ向きの1行」にする。
        ・同じ人の連投は5分以内ならアイコンと名前を省き、本文だけを続ける
        ・日付が変わる位置に区切り線を入れる
        ・行にホバーしたときだけ操作（削除）と、連投行の時刻を出す
      */}
      <div
        className={`rounded-sm border bg-white ${
          dragging ? 'border-wpaccent ring-2 ring-wpaccent/30' : 'border-wpborder'
        }`}
      >
        {thread.messages.length === 0 ? (
          <p className="py-10 text-center text-wptext-muted">メッセージはまだありません</p>
        ) : (
          <ul className="py-2">
            {thread.messages.map((message, index) => {
              const prev = index > 0 ? thread.messages[index - 1] : null;
              const newDay = !prev || dayKey(prev.created_at) !== dayKey(message.created_at);
              const grouped =
                !newDay &&
                prev !== null &&
                !prev.is_deleted &&
                !message.is_deleted &&
                prev.sender_user_id === message.sender_user_id &&
                new Date(message.created_at).getTime() - new Date(prev.created_at).getTime() <
                  GROUP_WINDOW_MS;
              const mine = message.sender_user_id === currentUserId;
              const readers = receipts.get(message.id) ?? [];
              const senderName = message.sender_name ?? '事務局';
              return (
                <Fragment key={message.id}>
                  {newDay && (
                    <li className="relative mx-4 my-3 border-t border-wpborder">
                      <span className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 rounded-full border border-wpborder bg-white px-3 py-0.5 text-xs font-semibold text-wptext-strong">
                        {formatDayLabel(message.created_at)}
                      </span>
                    </li>
                  )}
                  <li
                    className={`group relative flex gap-2 px-4 hover:bg-wpcanvas/60 ${
                      grouped ? 'py-0.5' : 'mt-1.5 py-1 first:mt-0'
                    } ${
                      // 自分が名指しされた行は左に印を付けて見つけやすくする（D-318）
                      message.mentioned_user_ids?.includes(currentUserId)
                        ? 'border-l-2 border-wpnotice-warning bg-wpnotice-warning/5'
                        : ''
                    }`}
                  >
                    {/* 左の固定幅ガター。先頭行はアイコン、連投行はホバー時だけ時刻を出す */}
                    <div className="w-9 shrink-0">
                      {grouped ? (
                        <span
                          className="hidden text-right text-xs text-wptext-muted group-hover:block"
                          title={formatDateTime(message.created_at)}
                        >
                          {formatTimeOnly(message.created_at)}
                        </span>
                      ) : (
                        <PhotoPreview
                          photoUrl={message.sender_photo_url}
                          name={senderName}
                          size={36}
                        />
                      )}
                    </div>

                    <div className="min-w-0 flex-1">
                      {!grouped && (
                        <p className="flex flex-wrap items-baseline gap-x-2">
                          <NameLink
                            name={senderName}
                            memberId={message.sender_member_id}
                            className="font-semibold text-wptext-strong hover:underline"
                          />
                          {message.sender_role && (
                            <span className="text-xs text-wptext-muted">
                              {ROLE_LABELS[message.sender_role]}
                            </span>
                          )}
                          <span
                            className="text-xs text-wptext-muted"
                            title={formatDateTime(message.created_at)}
                          >
                            {formatTimeOnly(message.created_at)}
                          </span>
                        </p>
                      )}

                      {message.is_deleted ? (
                        <p className="italic text-wptext-muted">このメッセージは削除されました</p>
                      ) : (
                        <>
                          {message.body && (
                            <p className="whitespace-pre-wrap break-words">
                              {renderBodyWithMentions(message.body, allNames, myName)}
                            </p>
                          )}
                          {message.attachment_url && (
                            <div className="mt-1">
                              {isImageFileName(message.attachment_name) ? (
                                <a
                                  href={message.attachment_url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="block"
                                >
                                  <img
                                    src={message.attachment_url}
                                    alt={message.attachment_name ?? '添付画像'}
                                    className="max-h-64 max-w-full rounded-sm border border-wpborder object-contain"
                                  />
                                </a>
                              ) : (
                                <a
                                  href={message.attachment_url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center gap-1 rounded-sm border border-wpborder px-2 py-1 text-wpaccent hover:bg-wpcanvas hover:underline"
                                >
                                  📎 {message.attachment_name ?? 'ファイル'}
                                  {message.attachment_size !== null && (
                                    <span className="text-xs text-wptext-muted">
                                      （{formatFileSize(message.attachment_size)}）
                                    </span>
                                  )}
                                </a>
                              )}
                            </div>
                          )}
                        </>
                      )}

                      {/*
                        押された絵文字と、リアクションを付ける入口（D-317・D-319）。
                        入口はホバーではなく**常時表示**にする。ホバーで出す方式は
                        「どこに操作があるか分からない」ため既にやめている（D-080）。
                      */}
                      {!message.is_deleted && (
                        <div className="mt-1 flex flex-wrap items-center gap-1">
                          {message.reactions.map((reaction) => (
                            <button
                              key={reaction.emoji}
                              type="button"
                              disabled={busy}
                              onClick={() => void onToggleReaction(message.id, reaction.emoji)}
                              title={`${reaction.names.join('、')}`}
                              className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs disabled:opacity-50 ${
                                reaction.reacted_by_me
                                  ? 'border-wpaccent bg-wpaccent/10 font-semibold text-wpaccent'
                                  : 'border-wpborder bg-white text-wptext hover:bg-wpcanvas'
                              }`}
                            >
                              <span>{reaction.emoji}</span>
                              <span className="tabular-nums">{reaction.count}</span>
                            </button>
                          ))}
                          <span className="relative">
                            <button
                              type="button"
                              onClick={() =>
                                setPickerFor((open) => (open === message.id ? null : message.id))
                              }
                              aria-expanded={pickerFor === message.id}
                              className="inline-flex items-center gap-1 rounded-full border border-dashed border-wpborder px-2 py-0.5 text-xs text-wptext-muted hover:border-wpaccent hover:text-wpaccent"
                            >
                              <span aria-hidden="true">☺</span>
                              <span>+</span>
                              <span className="sr-only">リアクションをつける</span>
                            </button>
                            {pickerFor === message.id && (
                              <span className="absolute bottom-full left-0 z-10 mb-1 flex items-center gap-1 rounded-sm border border-wpborder bg-white px-1.5 py-1 shadow-lg">
                                {REACTION_CHOICES.map((emoji) => (
                                  <button
                                    key={emoji}
                                    type="button"
                                    disabled={busy}
                                    onClick={() => {
                                      setPickerFor(null);
                                      void onToggleReaction(message.id, emoji);
                                    }}
                                    title={`${emoji} でリアクションする`}
                                    className="rounded-sm px-1 py-0.5 text-base hover:bg-wpcanvas disabled:opacity-50"
                                  >
                                    {emoji}
                                  </button>
                                ))}
                              </span>
                            )}
                          </span>
                        </div>
                      )}

                      {/* 既読の顔写真。Slackには無い表示だが本システムでは必要（D-145） */}
                      {readers.length > 0 && (
                        <div className="mt-0.5 flex items-center gap-1 text-xs text-wptext-muted">
                          <span>既読</span>
                          {readers.map((reader) => (
                            <span
                              key={reader.user_id ?? reader.name}
                              title={`${reader.name}さんが ${formatDateTime(reader.read_at)} に閲覧`}
                            >
                              <PhotoPreview
                                photoUrl={reader.photo_url}
                                name={reader.name}
                                size={16}
                              />
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* 自分の投稿の取り消しだけホバーで出す（誤って押しにくくするため） */}
                    {!message.is_deleted && mine && (
                      <div className="absolute right-3 top-0 hidden -translate-y-1/2 group-hover:block">
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => void onDelete(message)}
                          className="rounded-sm border border-wpborder bg-white px-2 py-1 text-xs text-wpnotice-error shadow-sm hover:bg-wpcanvas disabled:opacity-50"
                        >
                          削除
                        </button>
                      </div>
                    )}
                  </li>
                </Fragment>
              );
            })}
          </ul>
        )}
      </div>

      {thread.can_post ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void onSend();
          }}
        >
          <label className="sr-only" htmlFor="message-draft">
            メッセージ
          </label>
          {/* Slackと同じく、入力欄・添付・送信を1つの枠に収める */}
          <div className="relative rounded-sm border border-wpborder bg-white focus-within:border-wpaccent focus-within:ring-1 focus-within:ring-wpaccent">
            {/* @で参加者名の候補を出す（グループ内の宛先指示 / D-166）。
                Enter/Tabで先頭候補を確定、クリックでも選べる */}
            {mentionCandidates.length > 0 && (
              <ul className="absolute bottom-full left-0 z-10 mb-1 max-h-48 w-64 overflow-y-auto rounded-sm border border-wpborder bg-white shadow-lg">
                {mentionCandidates.map((name, index) => (
                  <li key={name}>
                    <button
                      type="button"
                      onClick={() => insertMention(name)}
                      className={`block w-full px-3.5 py-2 text-left hover:bg-wpcanvas ${
                        index === 0 ? 'bg-wpaccent/10 font-semibold' : ''
                      }`}
                    >
                      @{name}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <textarea
              id="message-draft"
              ref={textareaRef}
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                updateMentionQuery(e.target.value, e.target.selectionStart);
              }}
              onKeyDown={onDraftKeyDown}
              onClick={(e) =>
                updateMentionQuery(e.currentTarget.value, e.currentTarget.selectionStart)
              }
              onBlur={() => {
                // 候補のクリックを先に処理させてから閉じる
                setTimeout(() => setMentionQuery(null), 150);
              }}
              rows={2}
              className="block w-full resize-none border-0 bg-transparent px-3 py-2.5 focus:outline-none"
              placeholder="メッセージを入力（@で相手を指定できます）"
            />
            <div className="flex items-center justify-between gap-2 px-2 py-1.5">
              <div className="flex min-w-0 items-center gap-2">
                <label
                  htmlFor="message-file"
                  title="画像・ファイルを添付"
                  className="cursor-pointer rounded-sm px-2 py-1 text-wptext-muted hover:bg-wpcanvas hover:text-wptext-strong"
                >
                  📎<span className="sr-only">画像・ファイルを添付</span>
                </label>
                <input
                  ref={fileInputRef}
                  id="message-file"
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp,image/bmp,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                  className="sr-only"
                />
                {file && (
                  <span className="flex min-w-0 items-center gap-2 rounded-sm border border-wpborder px-2 py-1 text-xs">
                    {filePreviewUrl ? (
                      <img
                        src={filePreviewUrl}
                        alt={file.name}
                        className="h-8 w-8 shrink-0 rounded-sm object-cover"
                      />
                    ) : null}
                    <span className="truncate text-wptext-strong">
                      {file.name}（{formatFileSize(file.size)}）
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setFile(null);
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                      className="shrink-0 text-wpnotice-error hover:underline"
                    >
                      取り消す
                    </button>
                  </span>
                )}
              </div>
              <button
                type="submit"
                disabled={busy || (!draft.trim() && !file)}
                className="shrink-0 rounded-sm border border-transparent bg-wpaccent px-3.5 py-1.5 font-semibold text-white hover:bg-wpaccent-hover disabled:opacity-40"
              >
                送信
              </button>
            </div>
          </div>
          <p className="mt-1 text-xs text-wptext-muted">
            Enter で送信、Shift+Enter で改行。画像・ファイルはこの画面へドラッグしても添付できます。
          </p>
        </form>
      ) : (
        <p className="text-wptext-muted">
          {thread.is_closed
            ? 'このスレッドはクローズされているため投稿できません。'
            : 'このスレッドには返信できません。'}
        </p>
      )}
    </div>
  );
}
